import * as React from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import { Link } from 'gatsby';
import type { ProvidersResult, Slot } from 'booking/lib/types';
import {
    selectDate as createSelectDateAction,
    selectLocation,
    selectTime,
    setStepValidated,
    setNearbyAvailability,
    selectProvider,
    setDigitalWalkIn,
} from 'booking/redux/actions';
import { SelectedSlot } from 'booking/components/NearbyLocationsTimeSlots/NearbyLocation';
import useStep from 'booking/hooks/useStep';
import ConfirmModal from 'booking/components/ConfirmModal';
import useIsClosed from 'booking/hooks/useIsClosed';
import useIsToday from 'booking/hooks/useIsToday';
import { formatWeek, THRESHOLD_LOW_AVAILABILITY } from 'booking/lib/utils';
import { InputLabel, MenuItem, Select } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import type { LocationWithCoordinates } from 'lib/types';
import { useDigitalWalkInAvailability } from 'booking/hooks/useDigitalWalkInAvailability';
import useIsMobile from 'core/hooks/useIsMobile';
import { useLocation } from '@reach/router';
import { format, isValid, isFuture } from 'date-fns';
import * as styles from './DateForm.module.scss';
import useTimeSlots from './useTimeSlots';
import ChevronRight from './assets/chevron-right.inline.svg';
import ChevronLeft from './assets/chevron-left.inline.svg';
import Day from './Day';
import NearbySlotsProvider from '../NearbySlotsProvider';
import NoAvailabilityDateForm from '../NoAvailabilityDateForm';
import { REPLACE_DR_PREFIX_REGEX } from '../../lib/constants';
import WellnessWednesday, {
    Circle,
    useWellnessWednesday,
} from '../WellnessWednesday';
import useTrackBookingStep from '../../hooks/useTrackBookingStep';
import useExcludedVetspireProviderIds from '../../hooks/useExcludedVetspireProviderIds';
import useUpdateCityAndLocationId from '../../hooks/useUpdateCityAndLocationId';
import DigitalWalkInBooking from '../DigitalWalkInBooking';
import DigitalWalkInModal from '../DigitalWalkInModal';
import { useShouldShowDigitalWalkIn } from '../../hooks/useShouldShowDigitalWalkIn';

export type DateFormProps = {
    className?: string;
    location: LocationWithCoordinates;
};

export default function DateForm({
    className,
    location,
}: DateFormProps): React.ReactElement {
    const trackBookingStep = useTrackBookingStep();
    const trackSameDay = React.useCallback(() => {
        trackBookingStep('same_day');
    }, [trackBookingStep]);
    const excludedVetspireProviderIds = useExcludedVetspireProviderIds();
    const isMobile = useIsMobile();

    useUpdateCityAndLocationId();

    const locationId = location._id;

    const calendar = React.useRef<HTMLDivElement>(null);
    const [selectedNearbySlot, setSelectedNearbySlot] =
        React.useState<SelectedSlot | null>(null);

    const [digitalWalkInModalOpen, setDigitalWalkInModalOpen] =
        React.useState(false);

    const allowIndividualProviderBookings =
        location?.bookingSettings?.allowIndividualProviderBookings;

    const nearbyLocations = React.useMemo(
        () =>
            location?.nearestLocations?.map(
                (nearLocation) => nearLocation.location,
            ) ?? null,
        [location.nearestLocations],
    );

    const {
        month,
        week,
        weekControl: {
            next: nextWeek,
            previous: previousWeek,
            hasNext: hasNextWeek,
            hasPrevious: hasPreviousWeek,
        },
        monthControl: {
            next: nextMonth,
            previous: previousMonth,
            hasNext: hasNextMonth,
            hasPrevious: hasPreviousMonth,
        },
        daysOfTheWeek,
        selectDate,
        selectedDate,
        provider,
        setProvider,
        weekProviders,
        monthProviders,
        weekDays,
        monthDays,
        allSlots,
        ungroupedSlots,
        selectSlot,
        selectedSlot,
        isNextWeek,
        isLoadingDates,
        isLoadingSlots,
        selectedDateString,
        selectedDateStringWithDay,
        todayYYYYMMDD,
        timezone,
    } = useTimeSlots(locationId, isMobile);
    const providerList = isMobile ? weekProviders : monthProviders;
    const hasSelectedProvider = Boolean(provider?.id);

    const [providers, setProviders] = React.useState<ProvidersResult[]>([]);

    const digitalWalkInAvailability = useDigitalWalkInAvailability(locationId);

    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const initialDateParam = queryParams.get('time');

    React.useEffect(() => {
        if (initialDateParam) {
            const timestamp = Number(initialDateParam);
            const date = new Date(timestamp);
            if (!isValid(date) || !isFuture(date)) {
                return;
            }
            const formattedDate = format(date, 'yyyy-MM-dd');
            const dateSlot = date.toISOString();
            selectDate(formattedDate);
            selectSlot(dateSlot);
        }
    }, [initialDateParam, selectDate, selectSlot]);

    React.useEffect(() => {
        if (providerList) {
            setProviders([...providerList]);
        }
    }, [providerList]);

    // it's possible that the selected provider is not on this list if they don't
    // have availability this week. we explicitly add the provider to the list in that case
    React.useEffect(() => {
        const providerExists = providers.some(
            (p: ProvidersResult) => p.id === provider?.id,
        );
        if (!providerExists) {
            const selectedProviderResult = monthProviders?.find(
                (p) => p.id === provider?.id,
            );
            if (selectedProviderResult) {
                setProviders((prev: ProvidersResult[]) => [
                    ...prev,
                    selectedProviderResult,
                ]);
            }
        }
    }, [providers, monthProviders, provider]);

    const wellnessWednesday = useWellnessWednesday({
        week,
        month,
        customLocationPromoDays: location?.wellnessPromoDays,
    });

    const fullyBooked = allSlots?.length === 0;

    const filteredOutPlaceholdersFromProviders = React.useMemo(() => {
        return providers
            ?.filter(
                (providerToFilter) =>
                    !excludedVetspireProviderIds.includes(providerToFilter.id),
            )
            ?.sort((a, b) => {
                const lastNameA = a.name.split(' ').pop();
                const lastNameB = b.name.split(' ').pop();

                return lastNameA?.localeCompare(lastNameB ?? '') ?? 0;
            });
    }, [providers, excludedVetspireProviderIds]);

    const isToday = useIsToday(timezone, selectedDate);

    const isThisWeek = React.useMemo(
        () => !!weekDays.find((day) => day?.date === todayYYYYMMDD),
        [weekDays, todayYYYYMMDD],
    );
    const locationIdArray = React.useMemo(
        () => (locationId ? [locationId] : []),
        [locationId],
    );

    const isClosed = useIsClosed(locationIdArray, isToday, selectedDate);

    const shouldDisplayWalkInComponent = useShouldShowDigitalWalkIn(
        digitalWalkInAvailability,
        setDigitalWalkInModalOpen !== undefined,
        isToday,
        ungroupedSlots?.length ?? 0,
        hasSelectedProvider,
    );

    React.useEffect(() => {
        let handle: number | undefined;

        const handler = () => {
            if (calendar.current) {
                handle = requestAnimationFrame(() => {
                    handle = requestAnimationFrame(() => {
                        handle = undefined;
                    });
                });
            }
        };

        window.addEventListener('resize', handler);

        handler();

        return () => {
            window.removeEventListener('resize', handler);
            if (handle !== undefined) {
                cancelAnimationFrame(handle);
            }
        };
    }, []);

    const [goNextIntent, setGoNextIntent] = React.useState(false);
    const dispatch = useDispatch();

    const { next } = useStep(false);

    const handleSubmit = React.useCallback(
        (event?: React.FormEvent<HTMLFormElement>) => {
            if (event) {
                event.preventDefault();
            }

            if (selectedDate && selectedSlot) {
                dispatch(createSelectDateAction(selectedDate));
                dispatch(setStepValidated('date', true));
                dispatch(selectTime(selectedSlot));
                dispatch(selectProvider(provider));
                dispatch(setDigitalWalkIn(false));
                setGoNextIntent(false);
                next();
            }
        },
        [dispatch, next, selectedDate, selectedSlot, provider],
    );

    const handleNearbySubmit = React.useCallback(() => {
        if (selectedNearbySlot) {
            dispatch(selectLocation(selectedNearbySlot.locationId));
            dispatch(setNearbyAvailability(true));
            dispatch(createSelectDateAction(selectedDate));
            dispatch(selectTime(selectedNearbySlot.time));
            dispatch(setDigitalWalkIn(false));
            setGoNextIntent(false);
            setSelectedNearbySlot(null);
            dispatch(setStepValidated('date', true));
            next(selectedNearbySlot.citySlug, selectedNearbySlot.locationSlug);
        }
    }, [selectedNearbySlot, dispatch, selectedDate, next]);

    const handleDigitalWalkInSubmit = React.useCallback(
        (selectedTime: string) => {
            setDigitalWalkInModalOpen(false);
            if (selectedTime) {
                dispatch(createSelectDateAction(selectedTime));
                dispatch(setStepValidated('date', true));
                dispatch(selectTime(selectedTime));
                dispatch(selectProvider(null));
                dispatch(setDigitalWalkIn(true));
                setGoNextIntent(false);
                next();
            }
        },
        [dispatch, next],
    );

    React.useEffect(() => {
        if (!allowIndividualProviderBookings) {
            setProvider(null);
            dispatch(selectProvider(null));
        }
        if (selectedDate && selectedSlot && goNextIntent) {
            handleSubmit();
        }
    }, [
        selectedDate,
        selectedSlot,
        handleSubmit,
        goNextIntent,
        allowIndividualProviderBookings,
        setProvider,
        dispatch,
    ]);
    // don't flicker and render low availability form twice
    const showSlots = selectedDate !== '';
    const intl = useIntl();
    const selectSlotAndGoNext = React.useCallback(
        (time: string) => {
            selectSlot(time);
            setGoNextIntent(true);
        },
        [selectSlot],
    );
    const isLoading = isLoadingDates || isLoadingSlots;
    const numberOfUngroupedSlots =
        ungroupedSlots?.length ?? THRESHOLD_LOW_AVAILABILITY + 1;

    let slotsContent = null;
    const to = '/same-day';

    const renderNoAvailabilityForm = () => (
        <NoAvailabilityDateForm
            location={location}
            ungroupedSlots={ungroupedSlots}
            selectSlotAndGoNext={selectSlotAndGoNext}
            selectedSlot={selectedSlot}
            selectedDate={selectedDate}
            setSelectedNearbySlot={setSelectedNearbySlot}
            showHeader={numberOfUngroupedSlots === 0}
            isToday={isToday}
            isClosed={isClosed}
            isMobile={isMobile}
            shouldDisplayWalkInComponent={shouldDisplayWalkInComponent}
            setDigitalWalkInModalOpen={setDigitalWalkInModalOpen}
            eventPrefix="BF_date"
        />
    );

    const renderTodayAvailabilityMessage = () => (
        <p className={styles.todaySameDayAvailability}>
            {intl.formatMessage(
                { id: 'booking.form.date.todaySameDayAvailability' },
                {
                    span: (chunk) => (
                        <span className="show-only-above-md">{chunk}</span>
                    ),
                    link: (chunk) => (
                        <Link onClick={trackSameDay} to={to}>
                            {chunk}
                        </Link>
                    ),
                    br: <br />,
                },
            )}
        </p>
    );

    const renderSlotsButtons = (
        slots: readonly Slot[] | null,
        onClickHandler: (time: string) => void,
    ) => (
        <div className={styles.slotsButtons} data-cy="slots-buttons">
            {slots?.map(({ time, label }) => (
                <button
                    type="button"
                    data-cy="time"
                    onClick={() => onClickHandler(time)}
                    key={time}
                    className={classnames({
                        [styles.isSelected]: selectedSlot === time,
                    })}
                >
                    {label}
                </button>
            ))}
        </div>
    );

    const hasUngroupedSlots = (ungroupedSlots?.length ?? 0) > 0;

    if (!isLoading && !hasSelectedProvider && fullyBooked) {
        slotsContent = renderNoAvailabilityForm();
    } else if (
        showSlots &&
        !isLoadingDates &&
        !isLoadingSlots &&
        hasUngroupedSlots
    ) {
        slotsContent = (
            <div className={styles.slots}>
                <h4 className={styles.slotsHeader}>
                    {isToday
                        ? // eslint-disable-next-line max-len
                          `${intl.formatMessage({ id: 'booking.form.date.today' })}, ${selectedDateString}`
                        : selectedDateStringWithDay}
                </h4>
                {isToday && renderTodayAvailabilityMessage()}
                {renderSlotsButtons(ungroupedSlots, selectSlotAndGoNext)}
                {shouldDisplayWalkInComponent && (
                    <DigitalWalkInBooking
                        setDigitalWalkInModalOpen={setDigitalWalkInModalOpen}
                        eventPrefix="BF_date"
                    />
                )}
            </div>
        );
    } else if (hasSelectedProvider && fullyBooked) {
        slotsContent = renderNoAvailabilityForm();
    } else {
        slotsContent = (
            <div className={styles.slots}>
                {!isLoading && allowIndividualProviderBookings && (
                    <>
                        <h4 className={styles.noAvailabilityHeader}>
                            {intl.formatMessage({
                                id: 'booking.form.date.noAppointmentsAvailable',
                            })}
                        </h4>
                        <p
                            className={classnames(
                                styles.noAvailability,
                                styles.noAvailabilityText,
                            )}
                        >
                            {intl.formatMessage(
                                {
                                    id: 'booking.form.date.noIndividualProviderSlotsAvailableText',
                                },
                                {
                                    provider:
                                        provider && 'name' in provider
                                            ? provider?.name?.replace(
                                                  REPLACE_DR_PREFIX_REGEX,
                                                  '',
                                              )
                                            : '',
                                    location: location?.name,
                                    strong: ((chunks: React.ReactNode) => (
                                        <strong>{chunks}</strong>
                                    )) as unknown as React.ReactNode,
                                },
                            )}
                        </p>
                        <p className={styles.noAvailability}>
                            <strong>
                                {intl.formatMessage({
                                    id: 'booking.form.date.showingAllProviders',
                                })}
                            </strong>
                        </p>
                    </>
                )}
                {renderSlotsButtons(
                    allSlots,
                    allowIndividualProviderBookings
                        ? (time) => {
                              dispatch(selectProvider(null));
                              setSelectedNearbySlot({
                                  locationId,
                                  locationName: location?.name,
                                  time,
                                  provider: true,
                                  locationSlug: location?.shortSlug || '',
                                  citySlug: location.city?.slug || '',
                              });
                          }
                        : selectSlotAndGoNext,
                )}
            </div>
        );
    }

    const onSelectDate = React.useCallback(
        (date: string) => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    // clear selected slot, if date changed
                    if (selectedDate !== date && selectedSlot) {
                        dispatch(selectTime(''));
                        selectSlot('');
                    }
                    selectDate(date);
                });
            });
        },
        [dispatch, selectedDate, selectedSlot, selectSlot, selectDate],
    );

    const handleProviderSelect = (e: SelectChangeEvent<string>) => {
        const res = providers?.find(({ id }) => e.target.value === id);
        setProvider(res ? { id: res.id, name: res.name } : null);
    };

    const loadNearbySlots = !isLoading && showSlots && fullyBooked;
    const providersButtons = (
        <div className={styles.providers}>
            <InputLabel className={styles.label}>Choose a Provider</InputLabel>
            <Select
                value={provider?.id ?? 'any'}
                onChange={handleProviderSelect}
                label="Choose a Provider"
                className={styles.providersSelect}
                variant="standard"
                classes={{ icon: styles.icon }}
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                    classes: {
                        paper: styles.selectMenuPaper,
                    },
                }}
            >
                <MenuItem
                    value="any"
                    classes={{
                        root: styles.selectItem,
                        selected: styles.selectedItem,
                    }}
                >
                    First Available Provider
                </MenuItem>
                {filteredOutPlaceholdersFromProviders?.map((elem) => (
                    <MenuItem
                        key={elem.id}
                        value={elem.id}
                        classes={{
                            root: styles.selectItem,
                            selected: styles.selectedItem,
                        }}
                    >
                        Dr. {elem.name?.replace(REPLACE_DR_PREFIX_REGEX, '')}
                    </MenuItem>
                ))}
            </Select>
        </div>
    );

    const [touchStart, setTouchStart] = React.useState<number | null>(null);
    const [touchEnd, setTouchEnd] = React.useState<number | null>(null);

    // the required distance between touchStart and touchEnd to be detected as a swipe
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) =>
        setTouchEnd(e.targetTouches[0].clientX);

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) nextWeek();
        if (isRightSwipe) previousWeek();

        setTouchStart(null);
        setTouchEnd(null);
    };

    return (
        <NearbySlotsProvider
            nearbyLocations={nearbyLocations}
            day={selectedDate}
            loadSlots={loadNearbySlots}
        >
            <form
                className={classnames(className, styles.root)}
                onSubmit={handleSubmit}
            >
                <h3>
                    {/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore */}
                    {intl.formatMessage(
                        {
                            id: `booking.form.date.${
                                allowIndividualProviderBookings
                                    ? 'providersLabel'
                                    : 'label'
                            }`,
                        },
                        {
                            location: location?.name ?? '',
                            strong: (chunks: string) => (
                                <strong>{chunks}</strong>
                            ),
                        },
                    )}
                </h3>
                <div className={styles.grid}>
                    {isMobile && (
                        <div className={styles.providersButtons}>
                            {!isLoadingSlots && <>{providersButtons}</>}
                        </div>
                    )}
                    {wellnessWednesday?.showInfo && (
                        <WellnessWednesday
                            className={styles.promo}
                            daysOfWeek={wellnessWednesday.daysOfWeek}
                            copy={wellnessWednesday.copy}
                            link={wellnessWednesday.link}
                            hasMobileLink={wellnessWednesday.hasMobileLink}
                        />
                    )}
                    <div className={styles.calendar} ref={calendar}>
                        {!isMobile && (
                            <header>
                                {/* keeping this since we will most likely reimplement
                                hasPreviousYearMonth && (
                                    <button
                                        type="button"
                                        onClick={previousYearMonth}
                                        className={styles.previousYear}
                                    >
                                        <span>Go to previous year</span>
                                        <ChevronsLeft />
                                    </button>
                                ) */}
                                {hasPreviousMonth && (
                                    <button
                                        type="button"
                                        onClick={previousMonth}
                                        className={styles.previous}
                                    >
                                        <span>Go to previous month</span>
                                        <ChevronLeft />
                                    </button>
                                )}
                                <h4>{month.format('MMMM YYYY')}</h4>
                                {hasNextMonth && (
                                    <button
                                        type="button"
                                        onClick={nextMonth}
                                        className={styles.next}
                                        data-cy="nextMonth"
                                    >
                                        <span>Go to next month</span>
                                        <ChevronRight />
                                    </button>
                                )}
                                {/* keeping this since we will most likely reimplement
                                hasNextYearMonth && (
                                    <button
                                        type="button"
                                        onClick={nextYearMonth}
                                        className={styles.nextYear}
                                        data-cy="nextYear"
                                    >
                                        <span>Go to next year</span>
                                        <ChevronsRight />
                                    </button>
                                ) */}
                            </header>
                        )}
                        {isMobile && (
                            <header>
                                {/* keeping this since we will most likely reimplement
                                hasPreviousYearWeek && (
                                    <button
                                        type="button"
                                        onClick={previousYearWeek}
                                        className={styles.previousYear}
                                    >
                                        <span>Go to previous year</span>
                                        <ChevronsLeft />
                                    </button>
                                ) */}
                                {hasPreviousWeek && (
                                    <button
                                        type="button"
                                        onClick={previousWeek}
                                        className={styles.previous}
                                    >
                                        <span>Go to previous week</span>
                                        <ChevronLeft />
                                    </button>
                                )}
                                {isThisWeek && <h4>This Week</h4>}
                                {isNextWeek && <h4>Next Week</h4>}
                                {!isThisWeek && !isNextWeek && (
                                    <h4>{formatWeek(week)}</h4>
                                )}
                                {hasNextWeek && (
                                    <button
                                        type="button"
                                        onClick={nextWeek}
                                        className={styles.next}
                                        data-cy="nextWeek"
                                    >
                                        <span>Go to next week</span>
                                        <ChevronRight />
                                    </button>
                                )}
                                {/* keeping this since we will most likely reimplement
                                hasNextYearWeek && (
                                    <button
                                        type="button"
                                        onClick={nextYearWeek}
                                        className={styles.nextYear}
                                        data-cy="nextYear"
                                    >
                                        <span>Go to next year</span>
                                        <ChevronsRight />
                                    </button>
                                ) */}
                            </header>
                        )}
                        {isLoadingDates && (
                            <div data-cy="loading" className={styles.loading}>
                                <CircularProgress
                                    size={100}
                                    color="secondary"
                                    aria-label={intl.formatMessage({
                                        id: 'booking.form.date.loadingDates',
                                    })}
                                />
                            </div>
                        )}
                        <div data-cy="dates" className={styles.dates}>
                            {!isMobile &&
                                daysOfTheWeek.map((weekDay) => (
                                    <strong key={weekDay}>{weekDay}</strong>
                                ))}
                            {isMobile && (
                                <div className={styles.daysOfTheWeek}>
                                    {daysOfTheWeek.map((weekDay) => (
                                        <strong key={weekDay}>{weekDay}</strong>
                                    ))}
                                </div>
                            )}
                            {/* Mobile date selector - Week */}
                            {isMobile && (
                                <div
                                    className={styles.weekDays}
                                    onTouchStart={onTouchStart}
                                    onTouchMove={onTouchMove}
                                    onTouchEnd={onTouchEnd}
                                >
                                    <div className={styles.thisWeekDays}>
                                        {weekDays.map((day, idx) => (
                                            <Day
                                                key={`${
                                                    day?.date || ''
                                                }-${idx.toString(10)}`}
                                                day={day}
                                                onSelect={onSelectDate}
                                                isWellnessWednesday={
                                                    !!(
                                                        day &&
                                                        wellnessWednesday?.promoDays &&
                                                        wellnessWednesday?.promoDays.includes(
                                                            day.date,
                                                        )
                                                    )
                                                }
                                            >
                                                {day &&
                                                    wellnessWednesday?.promoDays &&
                                                    wellnessWednesday?.promoDays.includes(
                                                        day.date,
                                                    ) && (
                                                        <Circle
                                                            isSelected={
                                                                day.isSelected
                                                            }
                                                        />
                                                    )}
                                            </Day>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Desktop date selector - Month */}
                            {!isMobile &&
                                monthDays.map((day, idx) => (
                                    <Day
                                        key={`${day?.date || ''}-${idx.toString(
                                            10,
                                        )}`}
                                        day={day}
                                        onSelect={onSelectDate}
                                        isWellnessWednesday={
                                            !!(
                                                day &&
                                                wellnessWednesday?.promoDays &&
                                                wellnessWednesday?.promoDays.includes(
                                                    day.date,
                                                )
                                            )
                                        }
                                    >
                                        {day &&
                                            wellnessWednesday?.promoDays &&
                                            wellnessWednesday?.promoDays.includes(
                                                day.date,
                                            ) && (
                                                <Circle
                                                    isSelected={day.isSelected}
                                                />
                                            )}
                                    </Day>
                                ))}
                        </div>
                    </div>

                    {isLoadingSlots && (
                        <div className={styles.slotsWrapper}>
                            <div className={styles.slots}>
                                <div
                                    data-cy="loading"
                                    className={styles.loading}
                                >
                                    <CircularProgress
                                        size={100}
                                        color="secondary"
                                        aria-label={intl.formatMessage({
                                            id: 'booking.form.date.loadingSlots',
                                        })}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {!isLoadingSlots && (
                        <div className={styles.slotsWrapper}>
                            {allowIndividualProviderBookings && !isMobile && (
                                <>{providersButtons}</>
                            )}
                            {slotsContent}
                        </div>
                    )}
                </div>
                <DigitalWalkInModal
                    digitalWalkInSlots={digitalWalkInAvailability.data}
                    showModal={digitalWalkInModalOpen}
                    handleClose={() => setDigitalWalkInModalOpen(false)}
                    handleSubmit={handleDigitalWalkInSubmit}
                    eventPrefix="BF_date"
                />
                <ConfirmModal
                    showModal={!!selectedNearbySlot}
                    hideModal={() => setSelectedNearbySlot(null)}
                    handleSubmit={handleNearbySubmit}
                    selectedSlot={selectedNearbySlot}
                    timezone={timezone}
                />
            </form>
        </NearbySlotsProvider>
    );
}
